import { useMemo, useState } from 'react'
import { Upload } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  Modal,
  Textarea,
  useToast,
} from '@/components/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'

/**
 * Bulk-Import von Vertraegen aus CSV (Etappe 19).
 *
 * CSV-Format: erste Zeile = Spaltenueberschriften, danach Datenzeilen.
 * Erlaubte Spalten siehe BulkContractRow im Backend. Mindestens
 * `startDate` + (`partySapCardCode` oder `partyName`).
 *
 * Workflow: Upload → CSV-Parsen → Vorschau → Dry-Run → echter Import.
 */

interface BulkRow {
  [key: string]: string | number | boolean | null | undefined
}

interface BulkImportResult {
  inserted: number
  skipped: number
  errors: Array<{ row: number; message: string; data: BulkRow }>
  insertedIds: string[]
  dryRun: boolean
}

function parseCsv(text: string): BulkRow[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
  if (lines.length < 2) return []
  const headers = splitCsvLine(lines[0]!)
  const rows: BulkRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const values = splitCsvLine(lines[i]!)
    const row: BulkRow = {}
    for (let j = 0; j < headers.length; j++) {
      const key = headers[j]!.trim()
      const raw = values[j]?.trim() ?? ''
      if (raw === '') {
        row[key] = null
      } else if (raw === 'true' || raw === 'false') {
        row[key] = raw === 'true'
      } else if (/^-?\d+(\.\d+)?$/.test(raw) && (key.endsWith('Months') || key === 'noticePeriodDays')) {
        row[key] = Number(raw)
      } else {
        row[key] = raw
      }
    }
    rows.push(row)
  }
  return rows
}

function splitCsvLine(line: string): string[] {
  const out: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"'
        i++
      } else if (ch === '"') {
        inQuotes = false
      } else {
        current += ch
      }
    } else {
      if (ch === '"') inQuotes = true
      else if (ch === ',' || ch === ';') {
        out.push(current)
        current = ''
      } else {
        current += ch
      }
    }
  }
  out.push(current)
  return out
}

interface Props {
  open: boolean
  onOpenChange: (o: boolean) => void
}

export function BulkImportModal({ open, onOpenChange }: Props) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [csvText, setCsvText] = useState('')

  const parsed = useMemo(() => {
    if (!csvText.trim()) return [] as BulkRow[]
    try {
      return parseCsv(csvText)
    } catch {
      return []
    }
  }, [csvText])

  const importMutation = useMutation<BulkImportResult, Error, { dryRun: boolean }>({
    mutationFn: ({ dryRun }) =>
      apiClient.post('/contracts/bulk-import', { rows: parsed, dryRun }).then((r) => r.data),
    onSuccess: (r, vars) => {
      const tone = r.errors.length > 0 ? 'danger' : 'success'
      const action = vars.dryRun ? 'Dry-Run' : 'Import'
      toast({
        title: `${action}: ${r.inserted} OK · ${r.skipped} übersprungen · ${r.errors.length} Fehler`,
        tone,
      })
      if (!vars.dryRun) void qc.invalidateQueries({ queryKey: ['contracts'] })
    },
    onError: (err) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  function handleFile(file: File) {
    const reader = new FileReader()
    reader.onload = () => {
      setCsvText(typeof reader.result === 'string' ? reader.result : '')
    }
    reader.readAsText(file)
  }

  const previewRows = parsed.slice(0, 10)
  const headers = previewRows.length > 0 ? Object.keys(previewRows[0]!) : []

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Verträge aus CSV importieren"
      description="Spalten der ersten Zeile = Feldnamen (z. B. contractNumber, partySapCardCode, startDate). Mindestens startDate + partyName/partySapCardCode."
      size="lg"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={parsed.length === 0}
            loading={importMutation.isPending && importMutation.variables?.dryRun === true}
            onClick={() => importMutation.mutate({ dryRun: true })}
          >
            Dry-Run (nur prüfen)
          </Button>
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Upload className="w-4 h-4" />}
            disabled={parsed.length === 0}
            loading={importMutation.isPending && importMutation.variables?.dryRun === false}
            onClick={() => importMutation.mutate({ dryRun: false })}
          >
            {parsed.length} Verträge importieren
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleFile(file)
            }}
            className="text-small"
          />
          {parsed.length > 0 && <Badge variant="info" size="sm">{parsed.length} Zeilen erkannt</Badge>}
        </div>

        <Textarea
          label="… oder CSV direkt einfügen"
          rows={6}
          value={csvText}
          onChange={(e) => setCsvText(e.currentTarget.value)}
          placeholder={'contractNumber,partySapCardCode,partyName,startDate,billingCycle\n100015,C1234,Beispiel GmbH,2024-01-01,monthly'}
        />

        {parsed.length > 0 && (
          <Card>
            <Card.Header>
              <h4 className="text-small font-semibold text-gray-900">
                Vorschau (erste {previewRows.length} von {parsed.length} Zeilen)
              </h4>
            </Card.Header>
            <Card.Body className="overflow-x-auto">
              <table className="w-full text-caption">
                <thead className="text-gray-600 border-b border-gray-200">
                  <tr>
                    {headers.map((h) => (
                      <th key={h} className="text-left px-2 py-1 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      {headers.map((h) => (
                        <td key={h} className="px-2 py-1">{String(row[h] ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card.Body>
          </Card>
        )}

        {importMutation.data && (
          <Card className={importMutation.data.errors.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}>
            <Card.Body className="space-y-2">
              <p className="text-small font-medium text-gray-900">
                {importMutation.data.dryRun ? 'Dry-Run-Ergebnis' : 'Import abgeschlossen'}: {importMutation.data.inserted} OK · {importMutation.data.skipped} übersprungen · {importMutation.data.errors.length} Fehler
              </p>
              {importMutation.data.errors.length > 0 && (
                <ul className="text-caption text-amber-800 space-y-0.5 max-h-48 overflow-auto">
                  {importMutation.data.errors.slice(0, 50).map((e, i) => (
                    <li key={i}>
                      Zeile {e.row + 2}: {e.message}
                    </li>
                  ))}
                  {importMutation.data.errors.length > 50 && (
                    <li>… und {importMutation.data.errors.length - 50} weitere Fehler.</li>
                  )}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}
      </div>
    </Modal>
  )
}
